<template>
  <div>
    <div class="is-flex is-flex-direction-row is-flex-wrap-nowrap is-align-items-center">
      <b-button :type="onlyBroker ? 'is-primary' : ''" @click="onlyBroker = !onlyBroker">
        {{ $t("onlyBroker") }}
      </b-button>
      <div class="ml-5 mr-4" style="height: 1.5rem; border-right: 1px solid black" />
      <span>Nach dem</span>
      <b-field class="mb-0">
        <b-datepicker
          v-model="createdAfterDate"
          :show-week-number="true"
          locale="de-DE"
          placeholder="Click to select..."
          icon="calendar-today"
          class="ml-2"
          trap-focus
        />
      </b-field>
      <b-button class="is-success ml-4" :class="{ 'is-loading': csvIsLoading }" @click="runCSVExport()">
        {{ $t("exportCSVHeader") }}
      </b-button>
      <b-button
        :class="{ 'is-loading': csvIsLoading }"
        icon-left="download"
        class="is-success ml-4"
        @click="runAdminExport()"
      >
        {{ $t("exportAdmins") }}
      </b-button>
    </div>
    <Pagination
      :table-collection="organizationsQuery"
      sorting-field="created"
      :detailed="true"
      @details-open="(row) => detailsOpen.push(row.id)"
    >
      <template slot-scope="props">
        <b-table-column field="logo" label="" width="40">
          <b-icon icon="domain" size="is-medium" />
        </b-table-column>
        <b-table-column field="legalName" label="Name" :style2="'color:' + props.row.branding.primaryColor" sortable="">
          <span>
            <b>{{ props.row.legalName }}</b>
          </span>
          <br />
          <span v-if="props.row.isChildOf"> {{ $t("suborganization") }} {{ props.row.isChildOf }} </span>
        </b-table-column>
        <b-table-column label="">
          <span v-if="props.row.created"> NEU </span>
        </b-table-column>
        <b-table-column field="actions" label="Actions" numeric>
          <b-field class="is-pulled-right my-1" grouped group-multiline>
            <b-button
              v-if="!props.row.isChildOf"
              class="mr-4 is-secondary"
              @click="(modalActive = true), (selectedCompany = props.row.id)"
            >
              {{ $t("conditionsSettings") }}
            </b-button>
            <b-button
              class="mr-4 is-info"
              tag="router-link"
              :to="{
                name: 'Dashboard',
                params: { organizationId: props.row.id },
              }"
            >
              {{ $t("dashboardTitle") }}
            </b-button>
            <b-button
              v-if="!props.row.deactivated"
              class="is-warning"
              icon-right="pause"
              @click="deactivateOrg(props.row.id)"
            />
            <b-button v-else class="is-danger" icon-right="delete" @click="deleteOrg(props.row.id)" />
          </b-field>
        </b-table-column>
      </template>
      <template slot="detail" slot-scope="props">
        <OrganizationInfo v-if="detailsOpen.includes(props.row.id)" :props="props" />
      </template>
    </Pagination>
    <b-modal
      :active.sync="modalActive"
      has-modal-card
      keep-scroll="true"
      trap-focus
      :destroy-on-hide="true"
      aria-role="dialog"
      aria-modal
    >
      <ConditionsSettingsModal :organization-id="selectedCompany" :editable="true" />
    </b-modal>
  </div>
</template>

<script>
import OrganizationInfo from "@/components/OrganizationInfo.vue";
import ConditionsSettingsModal from "@/components/ConditionsSettingsModal";
import Pagination from "@/components/Pagination";
import { functions } from "@/firebase";

export default {
  name: "OrganizationsTable",
  components: {
    OrganizationInfo,
    Pagination,
    ConditionsSettingsModal,
  },
  props: {
    organizationsCollection: {
      type: Object,
      default() {
        return {};
      },
    },
  },
  data() {
    return {
      field: "legalName",
      platform: true,
      sortingField: "legalName",
      selectedCompany: "",
      modalActive: false,
      onlyBroker: false,
      onlyApplications: false,
      detailsOpen: [],
      csvIsLoading: false,
      createdAfterDate: null,
    };
  },
  computed: {
    organizationsQuery() {
      return this.onlyBroker
        ? this.onlyApplications
          ? this.organizationsCollection.where("isBroker", "==", true).where("brokerApproved", "==", false)
          : this.organizationsCollection.where("isBroker", "==", true)
        : this.organizationsCollection;
    },
  },
  methods: {
    async runCSVExport() {
      this.csvIsLoading = true;
      if (!this.createdAfterDate) {
        this.createdAfterDate = new Date("1970-01-01T00:00:00.000Z");
      }
      functions
        .httpsCallable("ExportRegisteredUsers")({
          type: "organizations",
          date: this.createdAfterDate.getTime(),
        })
        .then((csv) => {
          // download the file
          let encodeUri = encodeURI(csv.data);
          let link = document.createElement("a");
          link.setAttribute("href", encodeUri);
          link.setAttribute("download", "organizations.csv");
          document.body.appendChild(link);
          link.click();
          this.csvIsLoading = false;
        })
        .catch(() => {
          this.$buefy.toast.open({
            type: "is-danger",
            message: "Fehler beim Export",
          });
          this.csvIsLoading = false;
        });
    },
    async runAdminExport() {
      this.csvIsLoading = true;
      functions
        .httpsCallable("ExportAdmins")()
        .then((csv) => {
          console.log(csv);
          // download the file
          let encodeUri = encodeURI(csv.data);
          let link = document.createElement("a");
          link.setAttribute("href", encodeUri);
          link.setAttribute("download", "admins.csv");
          document.body.appendChild(link);
          link.click();
          this.csvIsLoading = false;
        })
        .catch(() => {
          this.$buefy.toast.open({
            type: "is-danger",
            message: "Fehler beim Exportieren",
          });
          this.csvIsLoading = false;
        });
    },
    async deactivateOrg(organizationId) {
      this.$buefy.dialog.confirm({
        title: this.$t("deactivateOrganization"),
        message: this.$t("deactivateOrganizationExplained"),
        confirmText: this.$t("deactivateOrganization"),
        cancelText: this.$t("cancel"),
        type: "is-warning",
        closeOnConfirm: false,
        hasIcon: true,
        onConfirm: async (str, component) => {
          try {
            await functions.httpsCallable("DeactivateOrganization")({
              organizationId: organizationId,
            });
            this.$buefy.toast.open({
              message: this.$t("organizationDeactivated"),
              type: "is-success",
            });
          } catch (err) {
            this.$buefy.toast.open(err.toString());
          }
          component.close();
          return;
        },
      });
    },
    async deleteOrg(organizationId) {
      this.$buefy.dialog.confirm({
        title: this.$t("deleteOrganization"),
        message: this.$t("deleteOrganizationExplained"),
        confirmText: this.$t("deleteOrganization"),
        cancelText: this.$t("cancel"),
        type: "is-danger",
        closeOnConfirm: false,
        hasIcon: true,
        onConfirm: async (str, component) => {
          try {
            await functions.httpsCallable("DeleteOrganization")({
              organizationId: organizationId,
            });
            this.$buefy.toast.open({
              message: this.$t("organizationDeleted"),
              type: "is-success",
            });
          } catch (err) {
            this.$buefy.toast.open(err.toString());
          }
          component.close();
          return;
        },
      });
    },
  },
};
</script>
